import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Monitor, Car, Heart, Brain, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const OmaghChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Relief Omagh | NHS-Complementary Pain Management</title>
        <meta
          name="description"
          content="NHS-complementary chronic pain hypnotherapy for Omagh via online sessions. Neuroscience-based approach for fibromyalgia, back pain, arthritis. GP collaboration. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Omagh, fibromyalgia hypnotherapy Tyrone, pain management Omagh, online pain therapy Omagh, NHS complementary Omagh" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/omagh-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Relief Omagh | NHS-Complementary Pain Management" />
        <meta property="og:description" content="NHS-complementary chronic pain hypnotherapy for Omagh via online sessions. Neuroscience-based approach for fibromyalgia, back pain, arthritis." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Treatment Omagh",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Omagh and County Tyrone via online sessions. Specializing in fibromyalgia, chronic back pain, arthritis, migraines with NHS-complementary approach and GP collaboration.",
            "url": "https://www.sharonclare-hypnotherapy.com/omagh-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Omagh",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Tyrone"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "postalCode": "BT7 2FZ",
                  "addressRegion": "County Down",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with chronic pain while I wait for NHS pain clinic?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. Hypnotherapy is an evidence-based complementary approach that works alongside NHS care, not as a replacement. With Western Trust pain clinic waiting times often exceeding 12-18 months for Omagh residents, hypnotherapy can provide relief while you wait for specialist appointments. Research shows hypnotherapy can reduce pain intensity by working with the nervous system to close the pain gate and reduce central sensitization. I work collaboratively with your GP and medical team to ensure coordinated care."
                }
              },
              {
                "@type": "Question",
                "name": "How does hypnotherapy work with my GP for chronic pain management?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I work collaboratively with your GP, not in competition. Hypnotherapy complements medical care by addressing the nervous system component of chronic pain. With 15 years nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your GP about your treatment. Many GPs welcome hypnotherapy as part of multidisciplinary pain management alongside physiotherapy, medication, and other treatments. Always inform your GP about any complementary therapies you're using."
                }
              },
              {
                "@type": "Question",
                "name": "Are online sessions as effective as in-person for chronic pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, research shows online hypnotherapy is equally effective as in-person sessions for chronic pain management. The therapeutic relationship, hypnosis techniques, and nervous system regulation work just as well via secure video call. For Omagh residents, online sessions eliminate the challenge of traveling with chronic pain while providing the same professional, evidence-based treatment. Many clients prefer online sessions for the convenience and comfort of receiving treatment at home."
                }
              },
              {
                "@type": "Question",
                "name": "What chronic pain conditions do you treat for Omagh clients?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I work with Omagh clients experiencing fibromyalgia, chronic back pain, arthritis, migraines and tension headaches, neuropathic pain, IBS pain, and other persistent pain conditions. The neuroscience-based approach calms the nervous system, reduces pain perception through descending pain inhibition, and helps your brain develop healthier pain processing patterns. The approach is particularly effective for pain conditions with a strong stress or trauma component, working alongside your NHS medical care."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy replace my pain medication?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No, hypnotherapy works alongside your medical treatment, not as a replacement. Many clients find they can reduce medication under medical supervision as their pain levels decrease, but any changes to medication must always be discussed with your doctor. The goal is to give you additional tools for pain management that work with your medical care from your GP and pain specialists, not to replace necessary medication. Always consult your GP before making any changes to prescribed medication."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions will I need for chronic pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients see significant improvement within 6-8 sessions for chronic pain management, though some notice changes sooner. Complex pain conditions or long-standing pain may benefit from 8-10 sessions. We'll create a personalized treatment plan during your initial consultation based on your specific pain condition, how long you've had it, and your goals. The 4-session package provides excellent value while ensuring commitment to the process."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Management for Omagh
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              NHS-complementary hypnotherapy serving Omagh via online sessions
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <Monitor className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Online sessions available</span> - No travel required
                </p>
              </div>
              <p className="text-gray-600 text-sm">In-person appointments at the Belfast clinic on the Ormeau Road or the Lisburn clinic at The Arzen Collective also available</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              NHS-Complementary Chronic Pain Hypnotherapy for Omagh
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Omagh and County Tyrone primarily via secure online sessions - no travel required. Most Omagh clients choose the convenience and accessibility of online hypnotherapy for chronic pain management. Research shows online sessions are equally effective as in-person treatment for pain conditions.
              </p>
              <p>
                Living with chronic pain in Omagh comes with unique challenges. Western Trust pain clinic waiting times often exceed 12-18 months, leaving many residents struggling with fibromyalgia, back pain, arthritis, or migraines while waiting for specialist support. The county town's rural location can make accessing specialist pain services difficult. Omagh's resilient community has faced significant challenges, and research shows clear links between historical trauma, stress, and chronic pain conditions.
              </p>
              <p>
                Hypnotherapy works alongside your NHS care, not as a replacement for medical treatment. I collaborate with your GP and medical team to provide complementary support while you wait for pain clinic appointments or alongside the treatment you're already receiving. Modern pain neuroscience shows us that chronic pain is processed in the nervous system - when we're stressed or anxious, pain signals intensify. By calming your fight-or-flight response and teaching your brain healthier pain processing patterns, many clients experience significant reductions in pain levels.
              </p>
              <p>
                With 15 years of nursing experience at Belfast City Hospital, I bring medical understanding to chronic pain treatment. I understand pain conditions from both a clinical and therapeutic perspective, I can communicate effectively with your GP, and I know the frustrations of lengthy NHS waiting times. Hypnotherapy offers evidence-based support that complements your medical care, helping you regain control and quality of life while working with your healthcare team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Chronic Pain */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              The Neuroscience of Chronic Pain
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Persistent pain lasting beyond 12 weeks isn't "all in your head," but it is in your nervous system. Understanding how pain works helps explain why hypnotherapy can be so effective.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Shield className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Central Sensitization</h3>
                <p className="text-gray-700">
                  The spinal cord and brain become hypersensitive, amplifying normal signals and interpreting them as pain. This is why pain can feel disproportionate to actual tissue damage, and why stress or trauma history can worsen pain intensity.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Fight-Flight-Freeze</h3>
                <p className="text-gray-700">
                  Ongoing stress, trauma history, or daily pressures keep your survival system on high alert, which keeps pain signals turned up. Calming this system is essential for pain reduction.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Neuroplasticity</h3>
                <p className="text-gray-700">
                  The brain learns pain pathways. The more they're used, the easier they fire. But neuroplasticity also means we can create new, healthier pathways through hypnotherapy.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                This is why you can feel real, distressing pain even when medical scans look "normal." The pain is genuine - your brain is simply protecting you a little too well. Hypnotherapy works by calming this overactive alarm system and helping your brain interpret sensations differently, working alongside your NHS medical care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Supports Pain Recovery
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center">
              Clinical hypnotherapy works directly with the mind-body connection to calm your overactive alarm system
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Shift from Danger to Safety</h3>
                    <p className="text-gray-700">
                      Calms the fight-flight response so your nervous system stops amplifying pain signals. This is particularly relevant for those dealing with historical trauma, ongoing stress, or community pressures that keep the nervous system on high alert.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Close the Pain Gate</h3>
                    <p className="text-gray-700">
                      Lowers the intensity of pain messages traveling through the spinal cord to the brain, reducing pain perception through descending pain inhibition pathways discovered in neuroscience research.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Harness Neuroplasticity</h3>
                    <p className="text-gray-700">
                      Creates new, healthier neural pathways so your brain can learn to interpret sensations differently. The brain's remarkable ability to change means chronic pain doesn't have to be permanent.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Practical Self-Management Tools</h3>
                    <p className="text-gray-700">
                      Self-hypnosis and relaxation techniques you can use at home to manage pain flare-ups, reduce stress, and improve sleep - all factors that influence pain levels.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                Research supports hypnotherapy's role in multidisciplinary pain care. A major review of over 7,000 children with chronic abdominal pain found hypnotherapy 68% more effective than no treatment - outperforming dietary and medication-based approaches.
              </p>
              <p className="text-gray-700 text-lg">
                For Omagh residents waiting 12-18 months for Western Trust pain clinic appointments, hypnotherapy offers evidence-based support that can provide relief while you wait for NHS specialist care, or work alongside the treatment you're already receiving from your GP.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NHS-Complementary Positioning */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Working Alongside Your NHS Care
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <span className="font-semibold">Hypnotherapy is complementary to NHS care, not a replacement for medical treatment.</span> With Western Trust pain clinic waiting times often exceeding 12-18 months, many Omagh residents need support now while waiting for specialist appointments. Hypnotherapy can provide that bridge, working alongside your GP care.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                I work collaboratively with your GP, pain specialists, physiotherapists, and other healthcare providers. Many chronic pain clinics now recommend hypnotherapy alongside physiotherapy, medication, mindfulness, or gentle movement programs. By addressing the nervous system directly, hypnotherapy enhances these treatments and helps you regain confidence, mobility, and quality of life. Your GP remains at the center of your care.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With 15 years of nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare team. I know the medications, the procedures, the specialist terminology, and the frustrations of chronic pain from both sides of the clinical encounter. I understand how NHS systems work and the challenges of rural healthcare access.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Medical Background</h3>
                <p className="text-gray-700">15 years Belfast City Hospital nursing experience brings clinical credibility to pain management</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">CNHC Registered</h3>
                <p className="text-gray-700">Professional registration ensures you receive evidence-based, ethical treatment</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">GP Collaboration</h3>
                <p className="text-gray-700">I work WITH your GP and medical team, not in competition with them</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Evidence-Based Approach</h3>
                <p className="text-gray-700">Neuroscience-backed techniques supported by research, not pseudoscience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Chronic Pain Conditions We Address
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Fibromyalgia</h3>
                <p className="text-gray-700">Widespread pain, fatigue, and sensitivity. Hypnotherapy addresses the nervous system sensitization underlying fibromyalgia symptoms and helps improve sleep quality.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Chronic Back Pain</h3>
                <p className="text-gray-700">Lower back pain, sciatica, disc problems. Calming stress and addressing trauma history significantly reduces back pain intensity through nervous system regulation.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Arthritis Pain</h3>
                <p className="text-gray-700">Osteoarthritis and rheumatoid arthritis pain management. Hypnotherapy helps reduce pain perception, improve mobility, and maintain daily activities.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Migraines & Headaches</h3>
                <p className="text-gray-700">Chronic migraine, tension headaches. Stress reduction and trigger management through hypnotherapy can reduce frequency and intensity significantly.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Neuropathic Pain</h3>
                <p className="text-gray-700">Nerve pain, burning sensations, numbness. Hypnotherapy works with pain gate control to reduce neuropathic pain signals effectively.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">IBS & Abdominal Pain</h3>
                <p className="text-gray-700">Irritable bowel syndrome pain and discomfort. Gut-brain connection responds particularly well to hypnotherapy, with strong research support.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Omagh Clients Choose Sharon Clare for Pain Management
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  15 Years Nursing Experience
                </h3>
                <p className="text-gray-700">
                  With 15 years at Belfast City Hospital, I understand pain conditions from a medical perspective. I've seen firsthand how chronic pain affects patients and families, the limitations of medication-only approaches, and how to work effectively with GPs and specialists. This medical background brings credibility and understanding to chronic pain treatment that Omagh clients value.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Trauma-Informed Care
                </h3>
                <p className="text-gray-700">
                  Understanding the impact of historical trauma and community resilience on chronic pain. Research shows clear links between post-conflict stress and chronic pain conditions. The solution-focused approach helps you move forward while addressing current pain, working with your body's capacity for healing and neuroplasticity.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Online Sessions - No Travel Required
                </h3>
                <p className="text-gray-700">
                  Secure online hypnotherapy sessions via video call mean no lengthy travel is required. Research shows online sessions are equally effective as in-person treatment for chronic pain. This is ideal for those with mobility limitations, severe pain days, or anyone who prefers the convenience of receiving professional treatment at home.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  GP Collaboration & NHS Partnership
                </h3>
                <p className="text-gray-700">
                  I work collaboratively with your GP and medical team, not as an alternative to NHS care. With my nursing background, I can communicate effectively with healthcare providers about your treatment. Many GPs welcome hypnotherapy as part of multidisciplinary pain management while you wait for pain clinic appointments or alongside existing NHS treatment.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Understanding Rural Healthcare Challenges
                </h3>
                <p className="text-gray-700">
                  Understanding the challenges of accessing specialist pain services from rural County Tyrone - long waiting times, travel distances to specialist clinics, limited local services. Online hypnotherapy provides accessible, professional pain management support without the barriers of distance.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Evidence-Based Neuroscience Approach
                </h3>
                <p className="text-gray-700">
                  Solution-focused hypnotherapy combines the latest pain neuroscience research with proven therapeutic techniques. Rather than dwelling on pain history, we focus on solutions, building resilience, and practical strategies for managing pain alongside your NHS care. This forward-focused approach supports healing and recovery.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Online Sessions */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Accessible Online Sessions for Omagh
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Monitor className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">Online Sessions - No Travel Required</h3>
                  <p className="text-gray-700 mb-2">
                    Secure video call sessions via Zoom or similar platform provide professional chronic pain hypnotherapy from the comfort of your home in Omagh. Research shows online hypnotherapy is equally effective as in-person sessions for pain management.
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Convenience:</span> No lengthy travel required
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Accessibility:</span> Ideal for those with mobility limitations or severe pain days
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Effectiveness:</span> Same professional treatment, evidence-based approach, therapeutic outcomes
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">In-Person Clinic Option</h3>
              <p className="text-gray-700 mb-2">
                If you prefer in-person sessions, our Belfast clinic is available. However, most Omagh clients choose online sessions for convenience and accessibility.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Omagh Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Living with fibromyalgia for 10 years, I'd tried everything my GP could offer. The online sessions with Sharon were perfect - no traveling when I was in pain. Her hypnotherapy approach helped where medication alone couldn't. My pain levels have reduced significantly and I'm sleeping better. Her nursing background meant she understood my condition completely."
                </p>
                <p className="font-semibold text-gray-900">C.M., Omagh - Fibromyalgia</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Waiting 18 months for the pain clinic was unbearable. My GP recommended trying hypnotherapy while I waited. The online sessions were convenient and effective. Sharon worked alongside my medical care, not instead of it. After 8 sessions, I've reduced my painkillers and I'm functioning so much better. Professional, evidence-based support."
                </p>
                <p className="font-semibold text-gray-900">P.D., Omagh - Chronic Back Pain</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Chronic migraines were controlling my life. Sharon's hypnotherapy approach addressed the stress and nervous system component my medication couldn't reach. Online sessions meant I could access professional help without the long drive. The frequency and intensity of my migraines have reduced dramatically. I feel like I have my life back."
                </p>
                <p className="font-semibold text-gray-900">S.H., Omagh - Chronic Migraines</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Omagh Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with chronic pain while I wait for NHS pain clinic?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. Hypnotherapy is an evidence-based complementary approach that works alongside NHS care, not as a replacement. With Western Trust pain clinic waiting times often exceeding 12-18 months for Omagh residents, hypnotherapy can provide relief while you wait for specialist appointments. Research shows hypnotherapy can reduce pain intensity by working with the nervous system to close the pain gate and reduce central sensitization. I work collaboratively with your GP and medical team to ensure coordinated, complementary care.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does hypnotherapy work with my GP for chronic pain management?
                </h3>
                <p className="text-gray-700">
                  I work collaboratively with your GP, not in competition. Hypnotherapy complements medical care by addressing the nervous system component of chronic pain that medication alone cannot fully address. With 15 years nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your GP about your treatment. Many GPs welcome hypnotherapy as part of multidisciplinary pain management alongside physiotherapy, medication, and other treatments. Always inform your GP about any complementary therapies you're using.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Are online sessions as effective as in-person for chronic pain?
                </h3>
                <p className="text-gray-700">
                  Yes, research shows online hypnotherapy is equally effective as in-person sessions for chronic pain management. The therapeutic relationship, hypnosis techniques, and nervous system regulation work just as well via secure video call. For Omagh residents, online sessions eliminate the challenge of traveling with chronic pain while providing the same professional, evidence-based treatment. Many clients prefer online sessions for the convenience and comfort of receiving treatment at home without the stress of travel.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What chronic pain conditions do you treat for Omagh clients?
                </h3>
                <p className="text-gray-700">
                  I work with Omagh clients experiencing fibromyalgia, chronic back pain, arthritis, migraines and tension headaches, neuropathic pain, IBS pain, and other persistent pain conditions. The neuroscience-based approach calms the nervous system, reduces pain perception through descending pain inhibition, and helps your brain develop healthier pain processing patterns. The approach is particularly effective for pain conditions with a strong stress or trauma component, working alongside your NHS medical care from your GP and specialists.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy replace my pain medication?
                </h3>
                <p className="text-gray-700">
                  No, hypnotherapy works alongside your medical treatment, not as a replacement. Many clients find they can reduce medication under medical supervision as their pain levels decrease through hypnotherapy, but any changes to medication must always be discussed with your doctor. The goal is to give you additional tools for pain management that work WITH your medical care from your GP and pain specialists, not to replace necessary medication. Always consult your GP before making any changes to prescribed medication.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I need for chronic pain management?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvement within 6-8 sessions for chronic pain management, though some notice changes sooner. Complex pain conditions or long-standing pain may benefit from 8-10 sessions. We'll create a personalized treatment plan during your initial consultation based on your specific pain condition, how long you've had it, and your goals. The 4-session package provides excellent value while ensuring commitment to the process and allowing time for meaningful neuroplastic change in pain processing patterns.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Take the First Step Toward Pain Relief"
        subtitle="If you're ready to explore how clinical hypnotherapy can complement your NHS care for chronic pain, book your initial consultation today and start retraining your brain to turn down the volume on pain."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default OmaghChronicPain;
